import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { LanguageService, UILanguage } from './language.service';
import { MoneyCommaPipe } from './money-comma.pipe';
import { MobileCartService } from './mobile-cart.service';
import { CustomerTier, MobileSessionService } from './mobile-session.service';
import { MobileCategory, MobileProduct, MobileProductsService, TierPriceRange } from './mobile-products.service';

type DashboardDictionary = {
  title: { en: string; nl: string; fr: string };
  hello: { en: string; nl: string; fr: string };
  tier: { en: string; nl: string; fr: string };
  filterLabel: { en: string; nl: string; fr: string };
  showFilters: { en: string; nl: string; fr: string };
  hideFilters: { en: string; nl: string; fr: string };
  clearFilters: { en: string; nl: string; fr: string };
  products: { en: string; nl: string; fr: string };
  image: { en: string; nl: string; fr: string };
  product: { en: string; nl: string; fr: string };
  categoriesLabel: { en: string; nl: string; fr: string };
  quantity: { en: string; nl: string; fr: string };
  packSize: { en: string; nl: string; fr: string };
  packIncrementHint: { en: string; nl: string; fr: string };
  tax: { en: string; nl: string; fr: string };
  taxAdded: { en: string; nl: string; fr: string };
  lineTotal: { en: string; nl: string; fr: string };
  cart: { en: string; nl: string; fr: string };
  cartEmpty: { en: string; nl: string; fr: string };
  clearCart: { en: string; nl: string; fr: string };
  loadMore: { en: string; nl: string; fr: string };
  logout: { en: string; nl: string; fr: string };
  order: { en: string; nl: string; fr: string };
  details: { en: string; nl: string; fr: string };
  ranges: { en: string; nl: string; fr: string };
  perUnit: { en: string; nl: string; fr: string };
  loading: { en: string; nl: string; fr: string };
};

const DICTIONARY: DashboardDictionary = {
  title: { en: 'Order Products', nl: 'Producten Bestellen', fr: 'Commander des Produits' },
  hello: { en: 'Hello', nl: 'Hallo', fr: 'Bonjour' },
  tier: { en: 'Tier', nl: 'Niveau', fr: 'Niveau' },
  filterLabel: { en: 'Category', nl: 'Categorie', fr: 'Categorie' },
  showFilters: { en: 'Show Categories', nl: 'Toon Categorieen', fr: 'Afficher les Categories' },
  hideFilters: { en: 'Hide Categories', nl: 'Verberg Categorieen', fr: 'Masquer les Categories' },
  clearFilters: { en: 'Clear Filters', nl: 'Filters Wissen', fr: 'Effacer les Filtres' },
  products: { en: 'Products', nl: 'Producten', fr: 'Produits' },
  image: { en: 'Image', nl: 'Afbeelding', fr: 'Image' },
  product: { en: 'Product', nl: 'Product', fr: 'Produit' },
  categoriesLabel: { en: 'Categories', nl: 'Categorieen', fr: 'Categories' },
  quantity: { en: 'Qty', nl: 'Aantal', fr: 'Qte' },
  packSize: { en: 'Pack Size', nl: 'Verpakkingseenheid', fr: 'Taille du Pack' },
  packIncrementHint: {
    en: 'Quantities round up to full packs of',
    nl: 'Aantallen worden naar boven afgerond op volle verpakkingen van',
    fr: 'Les quantites sont arrondies au pack complet de',
  },
  tax: { en: 'Tax', nl: 'Belasting', fr: 'Taxe' },
  taxAdded: { en: 'Tax Added', nl: 'Belasting Toegevoegd', fr: 'Taxe Ajoutee' },
  lineTotal: { en: 'Line Total', nl: 'Regeltotaal', fr: 'Total Ligne' },
  cart: { en: 'Cart', nl: 'Winkelwagen', fr: 'Panier' },
  cartEmpty: { en: 'Your cart is empty.', nl: 'Je winkelwagen is leeg.', fr: 'Votre panier est vide.' },
  clearCart: { en: 'Clear Cart', nl: 'Winkelwagen Legen', fr: 'Vider le Panier' },
  loadMore: { en: 'Load More', nl: 'Meer Laden', fr: 'Charger Plus' },
  logout: { en: 'Log Out', nl: 'Uitloggen', fr: 'Se Deconnecter' },
  order: { en: 'Add To Draft Order', nl: 'Voeg Toe Aan Conceptbestelling', fr: 'Ajouter au Brouillon' },
  details: { en: 'View Details', nl: 'Bekijk Details', fr: 'Voir Details' },
  ranges: { en: 'Price List', nl: 'Prijslijst', fr: 'Liste de Prix' },
  perUnit: { en: 'per unit', nl: 'per stuk', fr: 'par unite' },
  loading: { en: 'Loading dashboard products...', nl: 'Dashboardproducten laden...', fr: 'Chargement des produits...' },
};

@Component({
  selector: 'app-mobile-dashboard',
  standalone: true,
  imports: [CommonModule, MoneyCommaPipe],
  templateUrl: './mobile-dashboard.component.html',
  styleUrl: './mobile-dashboard.component.css',
})
export class MobileDashboardComponent {
  private readonly pageSize = 8;

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly languageService = inject(LanguageService);
  private readonly sessionService = inject(MobileSessionService);
  private readonly mobileProductsService = inject(MobileProductsService);
  private readonly cartService = inject(MobileCartService);
  private filterLoadingTimer: ReturnType<typeof setTimeout> | null = null;

  public readonly language = this.languageService.language;
  public readonly session = this.sessionService.session;
  public readonly loading = this.mobileProductsService.loading;
  public readonly error = this.mobileProductsService.error;
  public readonly products = this.mobileProductsService.products;
  public readonly cartCount = this.cartService.cartCount;

  public readonly visibleCount = signal<number>(this.pageSize);
  public readonly selectedCategoryIDs = signal<string[]>([]);
  public readonly categoryLoading = signal<boolean>(false);
  public readonly categoryFilterOpen = signal<boolean>(false);
  public readonly selectedQuantities = signal<Record<string, number>>({});
  public readonly inferredTier = signal<CustomerTier | null>(null);

  public readonly tier = computed<CustomerTier>(() => this.inferredTier() ?? 'bronze');
  public readonly categoryOptions = computed<MobileCategory[]>(() => {
    const map = new Map<string, MobileCategory>();
    for (const product of this.products()) {
      for (const category of product.categories || []) {
        if (!map.has(category.id)) {
          map.set(category.id, category);
        }
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      this.categoryName(a).localeCompare(this.categoryName(b), undefined, { sensitivity: 'base' }),
    );
  });
  public readonly filteredProducts = computed<MobileProduct[]>(() => {
    const selectedIDs = this.selectedCategoryIDs();
    if (!selectedIDs.length) {
      return this.products();
    }

    const selected = new Set(selectedIDs);

    return this.products().filter((product) =>
      (product.categories || []).some((category) => selected.has(category.id)),
    );
  });
  public readonly visibleProducts = computed<MobileProduct[]>(() =>
    this.filteredProducts().slice(0, this.visibleCount()),
  );
  public readonly canLoadMore = computed<boolean>(() =>
    this.visibleCount() < this.filteredProducts().length,
  );
  public readonly inferredDisplayName = computed<string>(() => {
    const email = (this.session()?.email ?? '').trim();
    if (!email) {
      return 'Customer';
    }

    const leftSide = email.split('@')[0] || email;
    return leftSide.replace(/[._-]+/g, ' ').trim() || email;
  });

  constructor() {
    if (!this.sessionService.isLoggedIn()) {
      void this.router.navigateByUrl('/mobile/login');
      return;
    }

    this.destroyRef.onDestroy(() => {
      if (this.filterLoadingTimer) {
        clearTimeout(this.filterLoadingTimer);
      }
    });

    void this.syncCustomerTier();

    void this.mobileProductsService.load();
  }

  public t(key: keyof DashboardDictionary): string {
    return DICTIONARY[key][this.language()];
  }

  public logout(): void {
    this.sessionService.logout();
    void this.router.navigateByUrl('/');
  }

  public goToCart(): void {
    void this.router.navigateByUrl('/mobile/cart');
  }

  public openProductDetails(product: MobileProduct): void {
    void this.router.navigateByUrl(`/mobile/products/${product.id}`);
  }

  public addToCart(product: MobileProduct): void {
    const productID = product.id;
    const productName = this.productName(product);
    const addQuantity = this.getSelectedQuantity(product);
    const existingLine = this.cartService.cartItems().find((item) => item.productID === productID);
    const mergedQuantity = (existingLine?.quantity ?? 0) + addQuantity;
    const unitPriceCents = this.unitPriceCents(product, mergedQuantity);
    const taxPercentage = Number(product.taxPercentage || 0);

    this.cartService.upsertItem({
      productID,
      productName,
      quantity: mergedQuantity,
      packageSize: product.packageSize,
      unitPriceCents,
      taxPercentage,
    });
  }

  public loadMore(): void {
    this.visibleCount.update((count) => count + this.pageSize);
  }

  public toggleCategoryFilter(rawCategoryID: string): void {
    const categoryID = String(rawCategoryID || '').trim();
    if (!categoryID) {
      return;
    }

    if (this.filterLoadingTimer) {
      clearTimeout(this.filterLoadingTimer);
    }

    this.categoryLoading.set(true);
    this.selectedCategoryIDs.update((current) => {
      if (current.includes(categoryID)) {
        return current.filter((id) => id !== categoryID);
      }
      return [...current, categoryID];
    });
    this.visibleCount.set(this.pageSize);

    this.filterLoadingTimer = setTimeout(() => {
      this.categoryLoading.set(false);
      this.filterLoadingTimer = null;
    }, 450);
  }

  public clearCategoryFilters(): void {
    if (!this.selectedCategoryIDs().length) {
      return;
    }
    this.selectedCategoryIDs.set([]);
    this.visibleCount.set(this.pageSize);
  }

  public isCategorySelected(categoryID: string): boolean {
    return this.selectedCategoryIDs().includes(categoryID);
  }

  public toggleCategoryFilterPanel(): void {
    this.categoryFilterOpen.update((open) => !open);
  }

  public productName(product: MobileProduct): string {
    return this.pickLocalizedValue(product.nameEnglish, product.nameDutch, product.nameFrench);
  }

  public productDescription(product: MobileProduct): string {
    return this.pickLocalizedValue(
      product.descriptionEnglish,
      product.descriptionDutch,
      product.descriptionFrench,
    );
  }

  public categoryName(category: MobileCategory): string {
    return this.pickLocalizedValue(category.nameEnglish, category.nameDutch, category.nameFrench);
  }

  public firstImage(product: MobileProduct): string {
    return product.imageLinks[0] || 'logo.png';
  }

  public tierPrice(range: TierPriceRange): number {
    return this.centsToEuro(this.mobileProductsService.tierPriceCents(range, this.tier()));
  }

  public getSelectedQuantity(product: MobileProduct): number {
    return this.normalizeQuantity(this.selectedQuantities()[product.id], product.packageSize);
  }

  public setSelectedQuantity(product: MobileProduct, rawValue: string): void {
    const parsed = Number(rawValue);
    const quantity = this.normalizeQuantity(parsed, product.packageSize);
    this.selectedQuantities.update((current) => ({
      ...current,
      [product.id]: quantity,
    }));
  }

  public packageSize(product: MobileProduct): number {
    return this.mobileProductsService.normalizePackageSize(product.packageSize);
  }

  public unitPriceForProduct(product: MobileProduct): number {
    return this.centsToEuro(this.unitPriceCents(product, this.getSelectedQuantity(product)));
  }

  public taxRate(product: MobileProduct): string {
    return `${Number(product.taxPercentage || 0).toFixed(2)}%`;
  }

  public taxAmountForProduct(product: MobileProduct): number {
    const quantity = this.getSelectedQuantity(product);
    return this.centsToEuro(this.taxAmountCents(product, quantity));
  }

  public lineTotalForProduct(product: MobileProduct): number {
    const quantity = this.getSelectedQuantity(product);
    return this.centsToEuro(this.lineTotalCents(product, quantity));
  }

  public hasPriceRanges(product: MobileProduct): boolean {
    return product.priceRanges.length > 0;
  }

  public categoryList(product: MobileProduct): string {
    if (!product.categories.length) {
      return '-';
    }

    return product.categories.map((category) => this.categoryName(category)).join(', ');
  }

  public trackByProductID(_index: number, product: MobileProduct): string {
    return product.id;
  }

  public trackByRange(_index: number, range: TierPriceRange): string {
    return `${range.start}-${range.end}`;
  }

  private normalizeQuantity(value: number | undefined, packageSize: number): number {
    return this.mobileProductsService.normalizeQuantity(Number(value), packageSize);
  }

  private unitPriceCents(product: MobileProduct, quantity: number): number {
    const targetQty = this.normalizeQuantity(quantity, product.packageSize);
    const matchingRange = product.priceRanges.find((range) => {
      const start = Math.min(range.start, range.end);
      const end = Math.max(range.start, range.end);
      return targetQty >= start && targetQty <= end;
    });

    if (!matchingRange) {
      if (!product.priceRanges.length) {
        return 0;
      }

      return this.mobileProductsService.tierPriceCents(product.priceRanges[0], this.tier());
    }

    return this.mobileProductsService.tierPriceCents(matchingRange, this.tier());
  }

  private taxAmountCents(product: MobileProduct, quantity: number): number {
    const normalizedQuantity = this.normalizeQuantity(quantity, product.packageSize);
    const subTotal = this.unitPriceCents(product, normalizedQuantity) * normalizedQuantity;
    return Math.round((subTotal * Number(product.taxPercentage || 0)) / 100);
  }

  private lineTotalCents(product: MobileProduct, quantity: number): number {
    const normalizedQuantity = this.normalizeQuantity(quantity, product.packageSize);
    return this.unitPriceCents(product, normalizedQuantity) * normalizedQuantity;
  }

  private centsToEuro(cents: number): number {
    return Number(this.mobileProductsService.centsToEuro(cents));
  }

  private pickLocalizedValue(english: string, dutch: string, french: string): string {
    const value = this.pickByLanguage(this.language(), english, dutch, french).trim();
    if (value !== '') {
      return value;
    }

    return english || dutch || french || '-';
  }

  private pickByLanguage(language: UILanguage, english: string, dutch: string, french: string): string {
    if (language === 'nl') {
      return dutch || english || french;
    }
    if (language === 'fr') {
      return french || english || dutch;
    }
    return english || dutch || french;
  }

  private async syncCustomerTier(): Promise<void> {
    const tier = await this.sessionService.loadCustomerTier(true);
    this.inferredTier.set(tier);
  }
}
